/**
 * DOM label layer over the map canvas. Our labels need JetBrains Mono tabular figures and
 * status chips, which map glyph fonts cannot provide, and must work with no network (offline
 * basemap has no glyph server). Label counts are small (tens), so projecting each on every
 * render frame is cheap; content is only rewritten when its key changes.
 */
import type { Map as MlMap } from 'maplibre-gl';

export interface LabelSpec {
  id: string;
  lon: number;
  lat: number;
  /** Rendered HTML (escaped by the caller via `esc`). */
  html: string;
  className: string;
  /** Pixel offset from the anchor point. */
  dx?: number;
  dy?: number;
  /** Hide below this zoom. */
  minZoom?: number;
  /** Higher draws above and wins label collisions. */
  z?: number;
  /** Horizontal anchoring of the label box relative to the point (+dx). Default 'left'. */
  anchor?: 'left' | 'right' | 'center';
  /** Never hidden by collisions (e.g. aircraft, selection). Pinned labels still try the other side of
   *  their point before overlapping something. */
  pinned?: boolean;
  /** Radius (px) of the map symbol this label belongs to; other labels keep off it. Default: derived
   *  from dx for point labels, 0 for centred ones. */
  sym?: number;
  /** Render height (m) of the point in 3D; projected by the layer's projector when one is set. */
  elevM?: number | null;
}

/** Screen point (CSS px) of an elevated point; null = fall back to the map's own projection. */
export type ElevatedProjector = (lon: number, lat: number, elevM: number) => { x: number; y: number } | null;

/** Map UI floating over the canvas (controls, chips, HUD, sheet clusters): labels keep out from under it. */
const KEEP_OUT =
  '.map-controls__group, .live__cluster .icon-btn, .live__cluster .mode-pill, .live__cluster .mark-btn, .live__cluster .setup-plus, .mark-strip, .live__overlays > *, .vischip, .alert-bell, .live__hud .live-hud, .live-panel, .live__rail > *, .live__modes > *, .map-edge, .live-cmd__selection';

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

interface Entry {
  el: HTMLDivElement;
  spec: LabelSpec;
  html: string;
  cls: string;
  visible: boolean;
  x: number;
  y: number;
  /** measured box; -1 = needs measuring */
  w: number;
  h: number;
}

export class LabelLayer {
  private root: HTMLDivElement;
  private groups = new Map<string, Map<string, Entry>>();
  private dirty = true;
  /** Groups not drawn at all (a viewing mode hides them). */
  private hidden: ReadonlySet<string> = new Set();
  /** Floating UI rects in label-layer coordinates, refreshed at most twice a second. */
  private keepOut: Array<[number, number, number, number]> = [];
  private keepOutAt = 0;
  private projector: ElevatedProjector | null = null;

  constructor(private map: MlMap) {
    this.root = document.createElement('div');
    this.root.className = 'map-labels';
    map.getCanvasContainer().appendChild(this.root);
  }

  /** Replace all labels of one group (e.g. 'aircraft'). */
  setGroup(group: string, specs: LabelSpec[]): void {
    let g = this.groups.get(group);
    if (!g) {
      g = new Map();
      this.groups.set(group, g);
    }
    const seen = new Set<string>();
    for (const s of specs) {
      seen.add(s.id);
      let e = g.get(s.id);
      if (!e) {
        const el = document.createElement('div');
        el.className = s.className;
        el.dataset.id = s.id;
        el.innerHTML = s.html;
        this.root.appendChild(el);
        e = { el, spec: s, html: s.html, cls: s.className, visible: true, x: NaN, y: NaN, w: -1, h: -1 };
        g.set(s.id, e);
      } else {
        if (e.html !== s.html) {
          e.el.innerHTML = s.html;
          e.html = s.html;
          e.w = -1;
        }
        if (e.cls !== s.className) {
          e.el.className = s.className;
          e.cls = s.className;
          e.w = -1;
        }
        e.spec = s;
      }
      if (s.z !== undefined) e.el.style.zIndex = String(s.z);
    }
    for (const [id, e] of g) {
      if (!seen.has(id)) {
        e.el.remove();
        g.delete(id);
      }
    }
    this.dirty = true;
  }

  /** Move a single label (per-frame aircraft updates) without touching content. */
  move(group: string, id: string, lon: number, lat: number, elevM?: number | null): void {
    const e = this.groups.get(group)?.get(id);
    if (!e) return;
    e.spec.lon = lon;
    e.spec.lat = lat;
    if (elevM !== undefined) e.spec.elevM = elevM;
    this.dirty = true;
  }

  /** 3D view: project labels that carry `elevM` through this (null = 2D, everything on the ground). */
  setProjector(p: ElevatedProjector | null): void {
    this.projector = p;
    this.dirty = true;
  }

  markDirty(): void {
    this.dirty = true;
  }

  /** Hide whole groups (e.g. 'cams' when a viewing mode hides cameras). */
  setHiddenGroups(groups: Iterable<string>): void {
    this.hidden = new Set(groups);
    this.dirty = true;
  }

  /**
   * Project and position every label, then declutter: labels are placed in priority order
   * (z, pinned first) and any label overlapping an already-placed one is hidden.
   */
  render(force = false): void {
    if (!this.dirty && !force) return;
    this.dirty = false;
    const zoom = this.map.getZoom();
    const w = this.root.clientWidth || this.map.getCanvas().clientWidth;
    const h = this.root.clientHeight || this.map.getCanvas().clientHeight;
    const cands: Array<{ e: Entry; x: number; y: number }> = [];
    for (const [name, g] of this.groups) {
      const off = this.hidden.has(name);
      for (const e of g.values()) {
        if (off) {
          this.hide(e);
          continue;
        }
        const s = e.spec;
        if (s.minZoom !== undefined && zoom < s.minZoom) {
          this.hide(e);
          continue;
        }
        const p = (s.elevM != null && this.projector?.(s.lon, s.lat, s.elevM)) || this.map.project([s.lon, s.lat]);
        const x = p.x + (s.dx ?? 0);
        const y = p.y + (s.dy ?? 0);
        if (x < -300 || y < -100 || x > w + 300 || y > h + 100) {
          this.hide(e);
          continue;
        }
        cands.push({ e, x, y });
      }
    }
    // measure boxes whose content changed (rare: layout read only then)
    for (const c of cands) {
      if (c.e.w < 0) {
        if (!c.e.visible) c.e.el.style.display = '';
        c.e.w = c.e.el.offsetWidth;
        c.e.h = c.e.el.offsetHeight;
        if (!c.e.visible) c.e.el.style.display = 'none';
      }
    }
    cands.sort((a, b) => Number(!!b.e.spec.pinned) - Number(!!a.e.spec.pinned) || (b.e.spec.z ?? 0) - (a.e.spec.z ?? 0));
    const PAD = 2;
    const overlaps = (r: readonly number[], q: readonly number[]) => r[0]! < q[2]! && r[2]! > q[0]! && r[1]! < q[3]! && r[3]! > q[1]!;
    // every point symbol is drawn whatever happens to its label: labels of equal or lower priority keep off it
    const syms: Array<{ e: Entry; r: [number, number, number, number] }> = [];
    for (const c of cands) {
      const s = c.e.spec;
      const rad = s.sym ?? (s.anchor === 'center' || /\bml-(ring|sun|pair)\b/.test(c.e.cls) ? 0 : Math.max(5, Math.abs(s.dx ?? 0) - 3));
      if (rad <= 0) continue;
      const px = c.x - (s.dx ?? 0);
      const py = c.y - (s.dy ?? 0);
      syms.push({ e: c.e, r: [px - rad, py - rad, px + rad, py + rad] });
    }
    const keepOut = this.keepOutRects();
    const placed: Array<[number, number, number, number]> = [];
    const blocked = (e: Entry, r: [number, number, number, number]) =>
      placed.some((q) => overlaps(r, q)) || keepOut.some((q) => overlaps(r, q)) || syms.some((q) => q.e !== e && (q.e.spec.z ?? 0) >= (e.spec.z ?? 0) && overlaps(r, q.r));
    for (const c of cands) {
      const s = c.e.spec;
      const bw = c.e.w;
      const bh = c.e.h;
      const dx = s.dx ?? 0;
      const dy = s.dy ?? 0;
      const px = c.x - dx;
      const py = c.y - dy;
      // where the label may go: its own side first, then mirrored across the point, then below / above
      const spots: Array<[number, number]> = [];
      if (s.anchor === 'center') spots.push([c.x - bw / 2, c.y - bh / 2]);
      else {
        const x0 = s.anchor === 'right' ? c.x - bw : c.x;
        spots.push([x0, c.y]);
        if (s.anchor !== 'right') spots.push([px - dx - bw, c.y]);
        else spots.push([px - dx, c.y]);
        const gap = Math.max(6, Math.abs(dx) - 2);
        spots.push([px - bw / 2, py + gap], [px - bw / 2, py - gap - bh]);
      }
      let chosen: [number, number, number, number] | null = null;
      for (let i = 0; i < spots.length; i++) {
        let [x, y] = spots[i]!;
        // keep labels of on-screen points inside the viewport horizontally
        if (px >= 0 && px <= w && bw < w - 8) x = Math.max(4, Math.min(w - bw - 4, x));
        const r: [number, number, number, number] = [Math.round(x) - PAD, Math.round(y) - PAD, Math.round(x) + bw + PAD, Math.round(y) + bh + PAD];
        if (!blocked(c.e, r)) {
          chosen = r;
          break;
        }
        if (i === 0 && s.pinned) chosen = r; // pinned: the home spot unless another one is clear
      }
      if (!chosen) {
        this.hide(c.e);
        continue;
      }
      placed.push(chosen);
      const x = chosen[0] + PAD;
      const y = chosen[1] + PAD;
      if (!c.e.visible) {
        c.e.el.style.display = '';
        c.e.visible = true;
      }
      if (x !== c.e.x || y !== c.e.y) {
        c.e.el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        c.e.x = x;
        c.e.y = y;
      }
    }
  }

  /** Floating UI over the map, in label coordinates (cached ~0.5 s: it moves rarely, and reading layout every frame is not free). */
  private keepOutRects(): Array<[number, number, number, number]> {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.keepOutAt < 500) return this.keepOut;
    this.keepOutAt = now;
    const scope = (this.root.closest('.live__map, .live, .mission-map') as HTMLElement | null) ?? this.root;
    const base = this.root.getBoundingClientRect();
    const out: Array<[number, number, number, number]> = [];
    scope.querySelectorAll<HTMLElement>(KEEP_OUT).forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      out.push([r.left - base.left, r.top - base.top, r.right - base.left, r.bottom - base.top]);
    });
    this.keepOut = out;
    return out;
  }

  private hide(e: Entry): void {
    if (e.visible) {
      e.el.style.display = 'none';
      e.visible = false;
    }
  }

  destroy(): void {
    this.root.remove();
    this.groups.clear();
  }
}
